import { MutationCache, QueryClient } from '@tanstack/react-query';
import {
  ApiError,
  shouldNotifyError,
  toUserMessage,
  toUserTitle,
} from '@platform/api-client';
import { showAlert } from '@platform/ui';

import { ONE_MINUTE_MS, STALE_TIME } from '@/shared/constants/app/time.constant';
import { HTTP_STATUS } from '@/shared/constants/app/http.constant';

/**
 * mutation meta 계약. admin-web 과 같은 규약(`skipGlobalError`)을 쓴다.
 *
 * ⚠️ `interface` 가 아니라 **`type` 이어야 한다.** TanStack v5 는 meta 가
 * `Record<string, unknown>` 에 할당 가능할 때만 그 타입을 채택하는데, interface 는
 * 암묵적 인덱스 시그니처가 없어 조용히 무시된다(에러 없이 타입 안전성만 사라진다).
 *
 * 그래서 base eslint 의 consistent-type-definitions(interface 강제)를 여기서만 끈다.
 * 규칙 자체를 완화하면 다른 곳의 진짜 위반까지 통과하므로 라인 단위로 예외 처리한다.
 */
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- TanStack meta 는 Record<string, unknown> 할당 가능해야 함
export type AppMutationMeta = {
  /**
   * 전역 에러 알림을 끈다.
   * 호출부가 **더 나은 문맥**(어디까지 진행됐는지 등)을 줄 수 있을 때만 사용한다.
   * 폼 인라인 에러로 보여주는 경우에도 켠다(팝업과 인라인이 겹치면 시끄럽다).
   */
  skipGlobalError?: boolean;
};

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: AppMutationMeta;
  }
}

/**
 * QueryClient.
 *
 * ── 전역 mutation 에러 알림 ──────────────────────────────────────────────
 * 이 MutationCache.onError 가 생기기 전에는, 호출부가 onError 를 깜빡하면 BE 가 내려준
 * 한글 message 가 콘솔에만 찍히고 화면은 무반응이었다. 그래서 **기본으로 알리고**,
 * 필요할 때만 `meta.skipGlobalError` 로 끈다.
 *
 * ⚠️ 이 핸들러는 `mutate()` / `mutateAsync()` **어느 쪽으로 호출하든** 실행된다.
 *   - 그래서 `mutateAsync` 를 catch 없이 써도 alert 자체는 뜬다.
 *     문제는 unhandled rejection 과, await 이후 코드가 조용히 중단되는 것.
 *   - 반대로 호출부에 onError 를 붙여도 이 핸들러는 그대로 돌아 **팝업이 2개**가 된다.
 *     직접 알리려면 반드시 `meta.skipGlobalError` 를 같이 켤 것.
 *
 * 콜백 실행 순서: MutationCache.onError → useMutation({onError}) → mutate(_, {onError})
 *
 * 쿼리에는 걸지 않는다. 백그라운드 refetch 마다 팝업이 뜨고, 비로그인 상태에서
 * 선택적 인증 API 가 401 을 흘리는 정상 흐름까지 알림이 되기 때문이다.
 * 조회 실패는 화면에서 `isError` 로 인라인 처리한다(문구는 동일하게 toUserMessage).
 *
 */
export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.skipGlobalError) return;
      // 취소·401 은 알리지 않는다(각각 화면 전환·로그인 리다이렉트가 처리).
      if (!shouldNotifyError(error)) return;

      void showAlert({
        title: toUserTitle(error),
        message: toUserMessage(error),
        tone: 'danger',
      });
    },
  }),
  defaultOptions: {
    queries: {
      // 기본은 1분 fresh — 슬라이스별 override 권장(STALE_TIME 프리셋 사용).
      // admin-web 은 운영 데이터라 더 짧은 STALE_TIME.MEDIUM 을 쓴다(의도된 차이).
      staleTime: ONE_MINUTE_MS,
      gcTime: STALE_TIME.LONG,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // 4xx(특히 401/403/404)는 재시도 의미 없음
        if (error instanceof ApiError && error.kind === 'SERVER') {
          if (
            error.status >= HTTP_STATUS.BAD_REQUEST &&
            error.status < HTTP_STATUS.INTERNAL_SERVER_ERROR
          ) {
            return false;
          }
        }
        return failureCount < 1;
      },
    },
    mutations: {
      retry: false,
    },
  },
});
